import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {ComponentDesc} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';

import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {PropertiesVisiblePopover} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Scrollable, ScrollBehavior} from '@mintdata-wsp/wsp/ui/components/scrollable/Scrollable';
import {ComponentInfo} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/properties/ComponentInfo';
import {PropertiesPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/properties/PropertiesPanel';
import {SurfaceKeyboardShortcuts} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceKeyboardShortcuts';
import {ComponentLayout} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/properties/layout/ComponentLayout';
import {ViewportLayout} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/properties/layout/ViewportLayout';
import {PropertyView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/properties/PropertyView';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, PopupSize, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {Binding} from '@mintdata/mint-kit/impl/properties/components/binding/Binding';
import {Checkbox, CheckboxStyle} from '@mintdata/mint-kit/impl/properties/components/checkbox/Checkbox';
import {Col} from '@mintdata/mint-kit/impl/properties/components/col/Col';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import {Row} from '@mintdata/mint-kit/impl/properties/components/row/Row';
import {Section} from '@mintdata/mint-kit/impl/properties/components/section/Section';
import {Toggler, TogglerPosition} from '@mintdata/mint-kit/impl/properties/components/toggler/Toggler';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import Dropzone from 'react-dropzone';

const styles = require('./SurfacePropertiesPanel.less');
const cx = classnames.bind(styles);

@observer
export class SurfacePropertiesPanel extends BaseComponentWithContext<any>
{
    private readonly _popoverId:GUID = IdUtil.newGUID();

    public render():JSX.Element
    {
        const {surfaceArea, surfacePropertiesPanel, surfaceFileUpload} = controllers.ui;
        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();

        const isDisableScroll:boolean = (
            surfacePropertiesPanel.propertiesVisiblePopover() !== PropertiesVisiblePopover.None
            || surfaceFileUpload.getIsFileDraggingOverPropertiesPanel()
        );

        if( surfaceArea.isInPreviewOrZenPreviewMode() )
            return (
                <Section className={styles.previewMode}>
                    Preview mode
                </Section>
            );

        if( selectedBoxes.length > 1 )
            return (
                <Section className={styles.previewMode}>
                    Select a single surface component to edit its properties
                </Section>
            );

        return (
            this._wrapIntoDropzone(
                <SurfaceKeyboardShortcuts>
                    <Scrollable
                        isScrollX={false}
                        isScrollY={!isDisableScroll}
                        behavior={ScrollBehavior.OnHover}
                        containerClassName={cx('scrollbarContainer')}
                        className={cx('scrollbar')}
                    >
                        <div className={styles.base}>
                            {
                                !surfaceArea.isInPreviewOrZenPreviewMode() &&
                                <PropertiesPanel>
                                    {selectedBoxes.length === 0 && this._renderViewportPropertiesForCurrentPage()}
                                    {selectedBoxes.length === 1 && this._renderComponentProperties(this._selectedBox)}
                                </PropertiesPanel>
                            }
                        </div>
                    </Scrollable>
                </SurfaceKeyboardShortcuts>
            )
        );
    }

    private _renderViewportPropertiesForCurrentPage()
    {
        const currentLayerPage:Page = controllers.ui.surfaceArea.getCurrentLayerPage();
        return currentLayerPage ? this._renderViewportProperties(currentLayerPage.artboard.viewport) : null;
    }

    @bind
    private _onClickIconDoc(e:React.MouseEvent<HTMLElement>, componentName:string):void
    {
        e.stopPropagation();
        e.preventDefault();

        const {lx} = controllers.ui;

        const dashedName = NameUtil.getDashedName(componentName);

        lx.showLxFrame();

        lx.postMessage({
            source: 'designer',
            event_type: 'show_component_docs',
            has_component_name: !!componentName,
            component_name: componentName ? dashedName : null
        });
    }

    private _renderComponentProperties(box:Box):JSX.Element
    {
        if( !box.component.ref.isResolved )
            return null;

        if( box.isViewport )
            return this._renderViewportProperties(box);

        const componentDesc = box.componentDesc;
        const componentConfig = componentDesc.componentConfig;

        const {surfacePropertiesPanel} = controllers.ui;
        const isCursorPropVisible = surfacePropertiesPanel.isCursorPropVisible(box);

        return (
            <div key={box.id}>
                <Section>
                    <div className={styles.componentInfoRow}>
                        <div className={styles.componentInfoWrapper}>
                            <ComponentInfo
                                name={componentConfig.name}
                                description={componentConfig.tip}
                                category={componentDesc.category.toString()}
                            />
                            <div
                                className={cx('iconDoc')}
                                onMouseDown={(e:React.MouseEvent<HTMLElement>) => this._onClickIconDoc(e, box.component.name)}
                            >
                                <WSPIcon
                                    name={WSPIcons.visualDesigner.spreadsheet.Help}
                                    size={WSPIconSize.medium20}
                                />
                            </div>
                        </div>
                        <div className={styles.lockWrapper}>
                            <PropertyView
                                hideTitle
                                componentDesc={componentDesc}
                                prop={componentDesc.lockProp}
                            />
                        </div>
                        <div className={styles.visibilityWrapper}>
                            <PropertyView
                                hideTitle
                                componentDesc={componentDesc}
                                prop={componentDesc.visibilityProp}
                            />
                        </div>
                    </div>
                </Section>
                <div ref={this._getPopoverPosition()}>
                    <Section>
                        <div className={styles.dataSettingsRow}>
                            {this._renderRepeaterToggler()}
                            {this._renderLoadingToggler()}
                            {this._renderEventsToggler(this._getEvents(box).length)}
                            {this._renderPopover(box)}
                        </div>
                    </Section>
                </div>
                {
                    Array.from(componentDesc.properties, ([key, prop]) => (
                        <PropertyView
                            key={key}
                            componentDesc={componentDesc}
                            prop={prop}
                        />
                    ))
                }
                {
                    componentConfig.supportsAutofocus &&
                    <PropertyView
                        hideTitle
                        componentDesc={componentDesc}
                        prop={componentDesc.autofocusProp}
                    />
                }
                <ComponentLayout componentDesc={componentDesc} />
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={componentDesc.opacityProp}
                />
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={componentDesc.rotationProp}
                />
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={componentDesc.scaleProp}
                />
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={componentDesc.perspectiveProp}
                />
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={componentDesc.skewProp}
                />
                {
                    (isCursorPropVisible) &&
                    <PropertyView
                        hideTitle
                        componentDesc={componentDesc}
                        prop={componentDesc.cursorProp}
                    />
                }
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={componentDesc.dndProp}
                />
                {
                    this._shouldRenderPopover() &&
                    <div className={styles.topBarPopoverOverlay} />
                }
            </div>
        );
    }

    private _renderViewportProperties(viewport:Box):JSX.Element
    {
        if( !viewport.component.ref.isResolved )
            return null;

        const componentDesc = viewport.componentDesc;
        const surfaceState:SurfaceState = controllers.ui.surfaceArea.getState();

        return (
            <div key={viewport.id}>
                <Section>
                    <div className={styles.componentInfoRow}>
                        <div className={styles.componentInfoWrapper}>
                            <ComponentInfo name={viewport.page.name} />
                        </div>
                    </div>
                </Section>
                <div ref={this._getPopoverPosition()}>
                    <Section>
                        <div className={styles.dataSettingsRow}>
                            {this._renderLoadingToggler()}
                            {this._renderEventsToggler(this._getEvents(viewport).length)}
                            {this._renderPopover(viewport)}
                        </div>
                    </Section>
                </div>
                {this._renderViewportLayoutProperties(viewport)}
                {
                    Array.from(componentDesc.properties, ([key, prop]) => (
                        <PropertyView
                            key={key}
                            componentDesc={componentDesc}
                            prop={prop}
                        />
                    ))
                }
                <PropertyView
                    hideTitle
                    componentDesc={componentDesc}
                    prop={surfaceState.propsByName.get(SystemPropsConfigs.SCROLLBAR_PROP_NAME)}
                />
                <ViewportLayout componentDesc={componentDesc} />
                {
                    this._shouldRenderPopover() &&
                    <div className={styles.topBarPopoverOverlay} />
                }
            </div>
        );
    }

    private _renderViewportLayoutProperties(viewport:Box):JSX.Element
    {
        const componentDesc = viewport.componentDesc;
        const surfaceState:SurfaceState = controllers.ui.surfaceArea.getState();

        return (
            <PropertyView
                hideTitle
                componentDesc={componentDesc}
                prop={surfaceState.propsByName.get(SystemPropsConfigs.VIEWPORT_LAYOUT_PROP_NAME)}
            >
                {[
                    <Row key={'positionElement'}>
                        <Col size={1}>
                            <PropertyView
                                hideTitle
                                provideTitleAsLabel
                                componentDesc={componentDesc}
                                prop={componentDesc.layoutLeftProp}
                            />
                        </Col>
                        <Col size={1}>
                            <PropertyView
                                hideTitle
                                provideTitleAsLabel
                                componentDesc={componentDesc}
                                prop={componentDesc.layoutTopProp}
                            />
                        </Col>
                        <Col size={1}>
                            <PropertyView
                                hideTitle
                                provideTitleAsLabel
                                componentDesc={componentDesc}
                                prop={componentDesc.layoutWidthProp}
                            />
                        </Col>
                        <Col size={1}>
                            <PropertyView
                                hideTitle
                                provideTitleAsLabel
                                componentDesc={componentDesc}
                                prop={componentDesc.layoutHeightProp}
                            />
                        </Col>
                    </Row>,
                    componentDesc,
                    componentDesc.layoutWidthProp.value.value.value,
                    componentDesc.layoutHeightProp.value.value.value
                ]}
            </PropertyView>
        );
    }

    private _getPopoverPosition():React.Ref<HTMLElement | SVGElement>
    {
        const {popupManager} = this.context;

        return popupManager.position(
            this._popoverId,
            SourceAnchor.LeftBottom,
            PopupAnchor.LeftTop,
            PopupSize.WidthOfSource
        );
    }

    private _renderRepeaterToggler():JSX.Element
    {
        const isRepeaterShown = controllers.ui.surfacePropertiesPanel.isRepeaterPropertiesVisible();

        return (
            <div className={styles.dataSettingsCol}>
                <Toggler
                    tooltip="Repeater"
                    text={this._renderRepeaterButtonText()}
                    icon={ImgIcons.repeater}
                    position={TogglerPosition.Single}
                    isPressed={isRepeaterShown}
                    onClick={this._onRepeaterClick}
                    isBlock
                    textClassName={styles.togglerBadge}
                />
                <div className={styles.note}>Repeater</div>
            </div>
        );
    }

    private _renderLoadingToggler():JSX.Element
    {
        const isLoaderShown = controllers.ui.surfacePropertiesPanel.isLoaderPropertiesVisible();

        return (
            <div className={styles.dataSettingsCol}>
                <Toggler
                    tooltip={'Loading'}
                    icon={ImgIcons.rotateRight}
                    position={TogglerPosition.Single}
                    isPressed={isLoaderShown}
                    onClick={this._onLoaderClick}
                    isBlock
                />
                <div className={styles.note}>Loading</div>
            </div>
        );
    }

    private _renderEventsToggler(eventsCount:number):JSX.Element
    {
        const isEventsShown = controllers.ui.surfacePropertiesPanel.isEventsPropertiesVisible();

        return (
            <div className={styles.dataSettingsCol}>
                <Toggler
                    tooltip={'Events'}
                    text={(eventsCount).toString()}
                    icon={ImgIcons.lightning}
                    position={TogglerPosition.Single}
                    isPressed={isEventsShown}
                    onClick={this._onEventsClick}
                    isBlock
                    textClassName={styles.togglerBadge}
                />
                <div className={styles.note}>Events</div>
            </div>
        );
    }

    @computed
    private get _selectedBox():Box
    {
        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();

        return selectedBoxes.length > 0 ? selectedBoxes[0] : null;
    }

    private _shouldRenderPopover():boolean
    {
        const {surfacePropertiesPanel} = controllers.ui;
        const isRepeaterShown = surfacePropertiesPanel.isRepeaterPropertiesVisible();
        const isLoaderShown = surfacePropertiesPanel.isLoaderPropertiesVisible();
        const isEventsShown = surfacePropertiesPanel.isEventsPropertiesVisible();

        return isRepeaterShown || isLoaderShown || isEventsShown;
    }

    private _renderPopover(box:Box)
    {
        const {surfacePropertiesPanel, bindings} = controllers.ui;

        if( !this._shouldRenderPopover() )
            return null;

        const {componentDesc} = box;
        const visiblePopover:PropertiesVisiblePopover = surfacePropertiesPanel.propertiesVisiblePopover();
        const isRepeaterShown = surfacePropertiesPanel.isRepeaterPropertiesVisible();
        const isLoaderShown = surfacePropertiesPanel.isLoaderPropertiesVisible();
        const isEventsShown = surfacePropertiesPanel.isEventsPropertiesVisible();
        const bindingState = bindings.getViewState();
        const eventsFiltered = this._getEvents(box);

        return (
            <Popover
                id={this._popoverId}
                isModal={false}
                ignoreEsc={true}
                onDismissed={this._onPopoverDismiss}
            >
                <div className={cx('topBarPopover', visiblePopover)}>
                    {isRepeaterShown && (
                        <div>
                            <Section>
                                <div className={styles.repeaterTitle}>Repeater</div>
                                <Row>
                                    <Col size={4}>
                                        <div className={styles.repeaterNote}>
                                            Repeat {componentDesc.componentConfig.name} based on data.
                                        </div>
                                    </Col>
                                </Row>
                            </Section>
                            <PropertyView
                                hideTitle
                                componentDesc={componentDesc}
                                prop={componentDesc.repeaterProp}
                            />
                        </div>
                    )}
                    {isLoaderShown && (
                        <PropertyView
                            hideTitle
                            componentDesc={componentDesc}
                            prop={componentDesc.loaderProp}
                        />
                    )}
                    {isEventsShown && (
                        <div data-component="event-panel">
                            <div>Events appear in output data table.</div>
                            <div className={styles.eventsList}>
                                {
                                    eventsFiltered.map((eventProp:EventProp, index:number) =>
                                    {
                                        const binding = !!eventProp.bindingId && bindings.getEventBinding(eventProp.bindingId);
                                        const isBound:boolean = !!binding;
                                        const isSelected:boolean = bindingState.currentEventProp === eventProp;

                                        const eventListItem = (
                                            <div className={styles.eventItem} data-prop={eventProp.name}>
                                                <Checkbox
                                                    isChecked={eventProp.isEnabled}
                                                    inlineLabel={eventProp.displayName}
                                                    style={CheckboxStyle.Toggler}
                                                    onClick={() => componentDesc.toggleEventState(eventProp)}
                                                />
                                                {isBound && (
                                                    <div className={styles.bindingExpr}>
                                                        {bindings.getBindingExpression(binding, null)}
                                                    </div>
                                                )}
                                            </div>
                                        );

                                        return (
                                            <div className={styles.eventListItemWrapper} key={index}>
                                                {isBound && <button
                                                    className={styles.eventStateHiddenToggler}
                                                    onClick={() => componentDesc.toggleEventState(eventProp)}
                                                />}

                                                <Binding
                                                    key={index}
                                                    target={eventListItem}
                                                    className={styles.eventListItem}
                                                    isBound={isBound}
                                                    isSelected={isSelected}
                                                    onBindClick={this._getEventBindHandler(eventProp)}
                                                    onUnBindClick={this._getEventUnbindHandler(eventProp)}
                                                    onSelect={this._getEventSelectHandler(eventProp)}
                                                />
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    )}
                    <div className={styles.arrowUp} />
                </div>
            </Popover>
        );
    }

    private _getEvents(box:Box):EventProp[]
    {
        const {componentDesc} = box;
        const surfaceState:SurfaceState = controllers.ui.surfaceArea.getState();

        let events:EventProp[] = box.isViewport
            ? [
                surfaceState.eventPropsByName.get(SystemPropsConfigs.APPLICATION_LOAD_EVENT_PROP_NAME),
                surfaceState.eventPropsByName.get(SystemPropsConfigs.VIEWPORT_RESIZE_EVENT_PROP_NAME),
                surfaceState.eventPropsByName.get(SystemPropsConfigs.POST_MESSAGE_PROP_NAME),
                ...Array.from(componentDesc.eventPropsMapByName.values())
            ]
            : [
                ...Array.from(componentDesc.eventPropsMapByName.values()),
                ...Array.from(componentDesc.baseEventPropsMapByName.values())
            ];

        return events.filter((eventProp:EventProp) => !eventProp.spec.isHideOnPropertyPanel);
    }

    private _wrapIntoDropzone(element:JSX.Element):JSX.Element
    {
        const {surfaceFileUpload, surfaceSelection, surfacePropertiesPanel} = controllers.ui;

        if( surfaceSelection.getSelectedBoxes().length !== 1 )
            return element;

        const isImageComponent:boolean = this._selectedBox.component.name === 'Image';
        const isFileDraggingOverPropertiesPanel:boolean = surfaceFileUpload.getIsFileDraggingOverPropertiesPanel();

        return (
            <Dropzone
                accept={'image/*'}
                multiple={false}
                onDragEnter={() => surfaceFileUpload.setIsFileDraggingOverPropertiesPanel(true)}
                onDragLeave={() => surfaceFileUpload.setIsFileDraggingOverPropertiesPanel(false)}
                onDrop={(a, r) => surfaceFileUpload.handleDroppedOnBoxFiles(a, r, this._selectedBox, surfacePropertiesPanel.getSelectedTabId())}
                disabled={!isImageComponent}
            >
                {({getRootProps, getInputProps}) => (
                    <div className={cx('fileDropzone')} {...getRootProps()}>
                        {element}
                        <div className={cx('dragOverPlaceholder', {isFileDraggingOverPropertiesPanel})} />
                    </div>
                )}
            </Dropzone>
        );
    }

    private _onRepeaterClick()
    {
        controllers.ui.surfacePropertiesPanel.toggleRepeaterPropertiesPopover();
    };

    private _onLoaderClick()
    {
        controllers.ui.surfacePropertiesPanel.toggleLoaderPropertiesPopover();
    };

    private _onEventsClick()
    {
        controllers.ui.surfacePropertiesPanel.toggleEventsPropertiesPopover();
    };

    private _onPopoverDismiss()
    {
        controllers.ui.surfacePropertiesPanel.setPropertiesPopover(PropertiesVisiblePopover.None);
    }

    private _getEventBindHandler(eventProp:EventProp)
    {
        return () =>
        {
            controllers.ui.bindings.startEventBinding(eventProp);
        };
    };

    private _getEventUnbindHandler(eventProp:EventProp)
    {
        return () =>
        {
            controllers.ui.bindings.unbindEventProp(eventProp);
        };
    };

    private _getEventSelectHandler(eventProp:EventProp)
    {
        return () =>
        {
            controllers.ui.bindings.editEventBinding(eventProp);
        };
    };

    @bind
    private _renderRepeaterButtonText():string
    {
        const componentDesc:ComponentDesc = this._selectedBox.componentDesc;
        const count:number = componentDesc.repeaterProp.value && componentDesc.repeaterProp.value.count
        && !componentDesc.repeaterProp.value.count.isError
            ? componentDesc.repeaterProp.value.count.value
            : 1;

        return count.toString();
    }
}
